import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";

/**
 * Tenant-wide FX context. Keeps a single base + rates map in memory.
 * Components subscribe via useFx() — call setBase(code) to switch the
 * "View all in" currency.
 */
const FxContext = createContext(null);

const LS_KEY = "pulse.fx.base";

export function FxProvider({ children, defaultBase = "USD" }) {
    const [base, setBaseState] = useState(() => {
        try {
            return localStorage.getItem(LS_KEY) || defaultBase;
        } catch (e) {
            return defaultBase;
        }
    });
    const [rates, setRates] = useState({}); // baseCurrency -> rates map (always normalized so 1 base = rates[code] units of code)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const loadRates = useCallback(async (b) => {
        if (rates[b]) return rates[b];
        setLoading(true);
        try {
            const { data } = await api.get(`/fx/rates?base=${encodeURIComponent(b)}`);
            setRates((prev) => ({ ...prev, [b]: data.rates || {} }));
            setError(null);
            return data.rates || {};
        } catch (e) {
            setError(e.response?.data?.detail || e.message);
            return null;
        } finally {
            setLoading(false);
        }
    }, [rates]);

    useEffect(() => {
        loadRates(base);
    }, [base, loadRates]);

    const setBase = useCallback((b) => {
        setBaseState(b);
        try { localStorage.setItem(LS_KEY, b); } catch (e) { /* localStorage unavailable */ }
    }, []);

    /**
     * Convert an amount from `from` currency into the current base.
     * Returns { amount, currency } — falls back to native if rate unavailable.
     */
    const toBase = useCallback((amount, from) => {
        if (!from || from === base) return { amount: Number(amount || 0), currency: base, native: true };
        const map = rates[base];
        if (!map) return { amount: Number(amount || 0), currency: from, native: true };
        // open.er-api returns rates where 1 base = rate[X] units of X.
        // So to convert FROM `from` to `base`, we need 1/rate[from].
        const rateFromBase = map[from];
        if (!rateFromBase) return { amount: Number(amount || 0), currency: from, native: true };
        return { amount: Number(amount || 0) / rateFromBase, currency: base, native: false };
    }, [base, rates]);

    return (
        <FxContext.Provider value={{ base, setBase, loadRates, toBase, loading, error, ratesForBase: rates[base] || {} }}>
            {children}
        </FxContext.Provider>
    );
}

export function useFx() {
    const ctx = useContext(FxContext);
    if (!ctx) throw new Error("useFx must be used within FxProvider");
    return ctx;
}
